import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MedicationSearchFormService } from './medication-search-form.service';
import { SearchObjectService, SearchObject } from '../../../../shared/services/search-object.service';
import { Subject, interval, empty, of } from 'rxjs';
import { switchMap, debounce, tap, takeUntil, catchError, map } from 'rxjs/operators';
import { Drugs, PrescriptionPlans } from './medication-search-form.interface';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { QueryTerms } from 'src/app/shared/models/QueryTerms';
import { SheetsService } from 'src/app/shared/components/sheets/sheets.service';

@Component({
  selector: 'app-medication-search-form',
  templateUrl: './medication-search-form.component.html',
  styleUrls: ['../search-form/search-form.component.scss', './medication-search-form.component.scss']
})
export class MedicationSearchformComponent implements OnInit, OnDestroy {
  @Input() searchObject: SearchObject;
  @Input() isLoginPage: boolean;
  networkIDChange$: Subject<boolean> = new Subject<boolean>();

  coverageTypeValue: string = undefined;
  coverageTypesData = [];

  error: any = {
    coverageTypeMsg: ''
  };

  @ViewChild('topSearchForm', { static: true }) topSearchForm: ElementRef;
  @ViewChild('drugInput', { static: false }) drugInput: ElementRef;

  benefitYear = new Date().getFullYear();
  drugsDebounce$ = new Subject<string>();
  toggleDrugs: Boolean = false;
  private destroy$ = new Subject<void>();
  drugErrorMsg = '';
  drugs: Drugs[] = [];
  drugsList: Drugs[] = [];
  navigationSubscription;
  prescriptionPlans: PrescriptionPlans[] = [];
  constructor(
    private searchObjectService: SearchObjectService,
    private apiService: MedicationSearchFormService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private sheetsService: SheetsService
  ) { }

  ngOnInit() {
    this.drugErrorMsg = '';
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      // If it is a NavigationEnd event re-initalise the component
      if (e instanceof NavigationEnd) {
        let paramMap: any = this.activatedRoute.snapshot.queryParamMap;
        if (this.activatedRoute.snapshot.queryParamMap.keys.length > 0 && paramMap.params && paramMap.params[QueryTerms.drugId] && paramMap.params[QueryTerms.drugName]) {
          this.searchObjectService.setSearchObjectQuery(paramMap);
          this.searchObject = this.searchObjectService.getSearchObject();
          this.callDrugsObservable(this.searchObject.drugName);
        }
      }
    });
    this.drugsDebounce$.pipe(
      debounce(value => value ? interval(500) : empty()),
      tap(value => this.searchObject['drugName'] = value),
      switchMap(this.callDrugsObservable.bind(this)),
      takeUntil(this.destroy$)
    ).subscribe(this.handleDrugsResponse.bind(this));
  }

  private callDrugsObservable(inputValue: string) {
    if (inputValue && inputValue.length >= 3) {
      if (this.drugsList.length > 0) {
        return of({ searchValue: inputValue, result: this.drugsList, errorMsg: '' });
      } else {

        return this.apiService.getDrugs(inputValue.toLowerCase(), this.searchObject.formularyId).pipe(
          // takeUntil(this.destroy$),
          map(result => {
            return ({ searchValue: inputValue, result: result['drugs'], errorMsg: '' });
          }),
          catchError(error => {
            return of(
              {
                searchValue: inputValue,
                result: [],
                errorMsg: this.apiService.createHttpErrorResponseMessage(error.status)
              }
            );
          })
        );
      }
    } else {
      return of({ searchValue: inputValue, result: [], errorMsg: '' });
    }
  }

  private handleDrugsResponse(
    response: { searchValue: string, result: Drugs[], errorMsg: string }) {
    if (response.errorMsg) {
      this.drugErrorMsg = response.errorMsg;
    } else {
      if (response.searchValue && response.searchValue.length >= 3 && !response.result.length) {
        this.drugErrorMsg = `Sorry, we don't have any matches for <strong>"${response.searchValue}"</strong>`;
      } else {
        this.drugErrorMsg = '';
      }
    }
    if (response.searchValue && response.searchValue.length >= 3) {
      this.drugsList = response.result;
      this.filterDrugs(response.searchValue);
    } else {
      this.drugs = [];
    }
  }

  filterDrugs(inputValue: string) {
    this.searchObject.drugId = null;
    this.searchObject.drugName = inputValue;
    this.drugs = this.drugsList.filter(d => {
      return (d.name.toLowerCase()).includes(inputValue.toLowerCase());
    });
    if (!this.drugs.length) {
      this.drugErrorMsg = `Sorry, we don't have any matches for <strong>"${inputValue}"</strong>`;
    } else {
      this.drugErrorMsg = '';
    }
  }

  setDrugs(drug: Drugs) {
    this.searchObject.drugId = drug.id;
    this.searchObject.drugName = drug.name;
    this.toggleDrugs = true;
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
    if (this.destroy$) {
      this.destroy$.unsubscribe();
    }
  }

  @Input()
  set coverageTypes(coverageTypes: any) {
    this.prescriptionPlans = [];
    if (this.searchObject.networkId) {
      this.coverageTypeValue = this.searchObject.networkId;
    } else {
      this.coverageTypeValue = undefined;
    }
    if (coverageTypes) {
      this.coverageTypesData = coverageTypes;
      // Show/Hide Radius and edit azure address settings based on isServiceAreaSearch
      if (this.coverageTypesData) {
        const nID = this.searchObject.networkId;
        const selectedCoverageType = this.coverageTypesData.filter((e) => {
          return nID === e.networkId;
        });

        if (selectedCoverageType.length > 0) {
          this.searchObject.isServiceAreaSearch = selectedCoverageType[0].isServiceAreaSearch;
          this.searchObject.coverageType = selectedCoverageType[0].name;
          selectedCoverageType[0].prescriptionPlans.forEach(pp => {
            if (!pp.formularyId) {
              pp.formularyId = 'AD_' + new Date().getTime();
            }
          });
          this.prescriptionPlans = selectedCoverageType[0].prescriptionPlans;
        }
      }

      // logged in member logic
      if (this.searchObject.memberObject.careTypes && this.searchObject.networkId) {
        this.searchObject.filters.subProductKey = undefined;
        for (let i = 0; i < this.searchObject.memberObject.careTypes.length; i++) {
          if (this.searchObject.networkId === this.searchObject.memberObject.careTypes[i].networkId) {
            if (this.searchObject.memberObject.careTypes[i].formularyId && this.searchObject.memberObject.careTypes[i].formularyDescription) {
              this.searchObject.formularyId = this.searchObject.memberObject.careTypes[i].formularyId;
              this.prescriptionPlans = [{
                name: this.searchObject.memberObject.careTypes[i].formularyDescription,
                formularyId: this.searchObject.memberObject.careTypes[i].formularyId,
                displayKey: null,
                subProductKey: null
              }];
            }
            break;
          }
        }
      }

      this.networkIDChange$.next(null);
    }
  }

  @Input()
  set clickedSearchCriteria(e) {
    if (e) {
      if (e === 'drug') {
        this.elementScroll(this.drugInput, true);
      } else {
        this.elementScroll(this.topSearchForm);
      }
    }
  }

  isShowbenefitYearOptions() {
    let currentDate = sessionStorage.getItem('benefitCurrentDate') ? new Date(sessionStorage.getItem('benefitCurrentDate')).getTime() : new Date().getTime(); 
    let month = ['0000010000010001700004', '0000010000010001700005'].includes(this.searchObject.networkId) ? 9 : 10;
    let startDate = new Date(new Date(currentDate).getFullYear(), month, 1, 0, 0, 0, 0).getTime();
    let endDate = new Date(new Date(currentDate).getFullYear(), 11, 31, 23, 59, 59, 59).getTime();
    if (!this.searchObject.benefitYear) {
      this.searchObject.benefitYear = this.benefitYear;
    }
    return ((startDate <= currentDate && endDate >= currentDate) && (['0000010000010001700004', '0000010000010001700005'].includes(this.searchObject.networkId)
      || ['0000010000010001700001', '0000010000010001700002', '0000010000010001700008'].includes(this.searchObject.networkId)));
  }

  onChangeBenefitYear(event: Event) {
    this.searchObject.benefitYear = Number((event.target as HTMLTextAreaElement).value);
    const coverageType = this.coverageTypesData.filter(e => e.networkId === this.searchObject.networkId);
    if (coverageType.length > 0) {
      this.prescriptionPlans = this.searchObject.benefitYear ? coverageType[0].prescriptionPlans.filter(item => {
        let year = item.name.replace(/^\D+/g, '');
        return !year || year == this.searchObject.benefitYear;
      }) : coverageType[0].prescriptionPlans;
    }
  }

  elementScroll(e: ElementRef, focus?: boolean) {
    e.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (focus) {
      e.nativeElement.focus({ preventScroll: true });
    }
  }


  _formValidation(type: string): boolean {
    if (this.searchObject.searchOption && (type === 'coverageType' || type === 'all')) {
      this.error.coverageTypeMsg = !this.searchObject.networkId ? 'Select the option that best describes how you get your insurance in this required field.' : '';
    }
    return this.error.coverageTypeMsg ? false : true;
  }

  updatePrescriptionPlans(event) {
    if (this.searchObject.formularyId !== event.target.value) {
      this.searchObject.drugName = '';
      this.drugsList = [];
    }
    this.searchObject.formularyId = event.target.value;
    this.searchObject.altDestinationUrl = undefined;
    if (this.searchObject.formularyId && this.searchObject.formularyId.includes('AD_')) {
      let selectedPrescriptionPlans = this.prescriptionPlans.find(pp => pp.formularyId === this.searchObject.formularyId);
      if (selectedPrescriptionPlans && selectedPrescriptionPlans.altDestinationUrl) {
        this.searchObject.altDestinationUrl = selectedPrescriptionPlans.altDestinationUrl;
      }
    };
  }

  updateCoverageType(event) {
    if (this.searchObject.networkId !== event.target.value) {
      this.searchObject.isServiceAreaSearch = false;
      this.searchObject.networkId = undefined;
      this.searchObject.altDestinationUrl = undefined;
      this.searchObject.searchYear = undefined;
      this.error.coverageTypeMsg = '';
      this.searchObject.searchOption = '';
      this.searchObject.formularyId = '';
      this.searchObject.drugName = '';
      this.searchObject.drugId = '';
      this.prescriptionPlans = [];
    }
    if (event.target.value) {
      const coverageType = this.coverageTypesData.filter(e => e.networkId === event.target.value);
      if (coverageType.length > 0) {

        this.searchObject.networkId = coverageType[0].networkId;
        this.coverageTypeValue = this.searchObject.networkId;
        this.searchObject.isServiceAreaSearch = coverageType[0].isServiceAreaSearch;
        this.searchObject.coverageType = coverageType[0].name;
        this.searchObject.isMultiSpecialtySearch = coverageType[0].isMultiSpecialtySearch;
        coverageType[0].prescriptionPlans.forEach(pp => {
          if (!pp.formularyId) {
            pp.formularyId = 'AD_' + new Date().getTime();
          }
          if (pp.formularyId) {
            pp.formularyId = pp.displayKey + 'displayKey_' + pp.formularyId;
          }
        });
        if (!this.searchObject.benefitYear) {
          this.searchObject.benefitYear = this.benefitYear;
        }
        this.prescriptionPlans = this.searchObject.benefitYear ? coverageType[0].prescriptionPlans.filter(item => {
          let year = item.name.replace(/^\D+/g, '');
          return !year || year == this.searchObject.benefitYear;
        }) : coverageType[0].prescriptionPlans;
      } else {
        this.coverageTypeValue = undefined;
      }
    } else {
      this.coverageTypeValue = undefined;
    }
    this._formValidation('coverageType');
    this.networkIDChange$.next(null);
  }

  /**
    * @TODO Figure out what's up with this method returning true
    * @param event
    * @param inputValue
    * @returns
    */
  getDrugs(event, inputValue: string) {
    if (event.keyCode === 38 || event.keyCode === 40 || event.keyCode === 13 || event.keyCode === 9) {
      return true;
    }
    this.drugsDebounce$.next(inputValue);
    this.toggleDrugs = false;
    // @TODO is this right?
    return false;
  }

  isDisabledSearchBtn(): boolean {
    let isDisabled = false;
    if ((!this.searchObject.networkId || !this.searchObject.formularyId || !this.searchObject.drugId)) {
      isDisabled = true;
    }
    return isDisabled;
  }

  openHoverSheet(event, title, message) {
    // sheet function
    if (!this.apiService.isDesktop()) {
      this.sheetsService.changeSheets({
        isMessage: true,
        title: title,
        message: message
      });
      event.stopPropagation();
    }
  }


  submitSearch() {
    // this.searchObject.formularyId = '1';
    if (!this.isShowbenefitYearOptions()) {
      this.searchObject.benefitYear = new Date().getFullYear();
    } else if (!this.searchObject.benefitYear) {
      this.searchObject.benefitYear = new Date().getFullYear();
    }
    this.searchObjectService.setSearchObject(this.searchObject);
    this.searchObjectService.setSearchURL(false, this.isLoginPage, true);
  }

  resetSearchForm() {
    this.error.specialtyOptionsMsg = '';
  }

}

